import React from 'react';
import { Box, Button } from '@mui/material';

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
  errorStack: string;
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMessage: '', errorStack: '' };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      errorMessage: error?.message || 'Unknown error',
      errorStack: error?.stack || '',
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('App crashed:', error, info.componentStack);
  }

  handleReload = () => {
    window.location.href = '/dashboard';
  };

  handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/landing';
  };

  render() {
    if (this.state.hasError) {
      // Sits outside the theme, so every colour here is explicit (instrument panel, 2026-11-03).
      const mono = "'JetBrains Mono', monospace";
      const btn = { minHeight: 40, px: 2, borderRadius: '8px', fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', fontWeight: 700, textTransform: 'none' as const };
      return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, background: '#0a0e14' }}>
          <Box sx={{ width: '100%', maxWidth: 420, position: 'relative', overflow: 'hidden', borderRadius: '12px', border: '1px solid rgba(255,23,68,0.4)', background: '#111823', p: 2,
            '&::before': { content: '""', position: 'absolute', top: 0, left: 16, right: 16, height: 2, background: '#FF1744', boxShadow: '0 0 12px #FF1744' } }}>
            <Box sx={{ fontFamily: mono, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 2, color: '#FF1744' }}>SOMETHING WENT WRONG</Box>
            <Box sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.85)', mt: 0.75, lineHeight: 1.45 }}>This screen hit an error. Reload to try again.</Box>
            <Box sx={{ mt: 1.25, p: 1.25, borderRadius: '8px', background: 'rgba(255,23,68,0.06)', border: '1px solid rgba(255,23,68,0.2)', maxHeight: 160, overflow: 'auto',
              fontFamily: mono, fontSize: '0.7rem', color: '#FF8A9B', wordBreak: 'break-word' }}>
              {this.state.errorMessage}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button onClick={this.handleReload} sx={{ ...btn, background: '#00E0FF !important', color: '#0a0e14 !important' }}>Reload</Button>
              <Button onClick={this.handleLogout} sx={{ ...btn, background: 'transparent !important', color: '#00E0FF !important', border: '1px solid rgba(0,224,255,0.5) !important' }}>Sign out</Button>
            </Box>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}
